import { BASE_URL } from "@/constants/endpoints";
import {
  AccountData,
  AccountListEntity,
} from "@/pages/accounts/schema/account.schema";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import { AxiosInstance } from "axios";

export interface GetAccountListProps {
  "P.Pagina": number;
  "P.Records": number;
  NroCuenta: string;
  ZonaComercial: string;
  Localidad: string;
  RazonSocial: string;
  TipoDeCuenta: string;
  ControlDeSaldos: boolean;
}

export interface GetProductoresProps {
  "P.Pagina": number;
  "P.Records": number;
  NroCuenta?: number;
  ZonaComercial?: string;
  Localidad?: string;
  RazonSocial?: string;
  TipoDeCuenta?: string;
}

export interface GetAccountListResponse {
  totalPages: number;
  totalRecords: number;
  listado: AccountListEntity[];
}

let accountsApiPromise: Promise<AxiosInstance> | null = null;

const getAccountsApi = async () => {
  if (accountsApiPromise) return accountsApiPromise;

  accountsApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return accountsApiPromise;
};

export const getAccounts = async (
  props: GetAccountListProps,
): Promise<GetAccountListResponse | null> => {
  const api = await getAccountsApi();
  if (!api) return null;
  const response = await api.get(
    `/Cuentas/GetListadoCuentas?${objectToQueryStringWithArray(props)}`,
  );
  return response.data;
};

export const getProductores = async (
  props: Partial<GetProductoresProps>,
): Promise<GetAccountListResponse | null> => {
  const api = await getAccountsApi();
  if (!api) return null;
  const response = await api.get(
    `/Cuentas/GetListadoProductores?${objectToQueryStringWithArray(props)}`,
  );
  return response.data;
};

interface GetAccountDetailProps {
  NroCuenta: number;
}

export const getAccountData = async (
  props: GetAccountDetailProps,
): Promise<AccountData | null> => {
  const api = await getAccountsApi();
  if (!api) return null;
  const response = await api.get(
    `/Cuentas/GetDetalleCuenta?${objectToQueryStringWithArray(props)}`,
  );
  return response.data;
};

export interface GetCuentasByBusquedaProps {
  busqueda: string;
}

export interface CuentasByBusquedaEntity {
  titular: string;
  tipoCuenta: string;
  idTipoCuenta: number;
  nroCuenta: string;
  zonaComercial: string
  cuit: string
}

export const getCuentasByBusqueda = async (
  props: GetCuentasByBusquedaProps,
): Promise<CuentasByBusquedaEntity[] | null> => {
  const api = await getAccountsApi();
  if (!api) return null;
  const response = await api.get(
    `/Cuentas/GetCuentasByBusqueda?${objectToQueryStringWithArray(props)}`,
  );

  return response.data;
};

export const getTiposProveedor = async (): Promise<TipoProveedor[] | null> => {
  const api = await getAccountsApi();
  if (!api) return null;
  const response = await api.get(`/Cuentas/GetTiposProveedor`);

  return response.data;
};

export interface TipoProveedor {
  id: number;
  nombre: string;
}

export interface Sucursal {
  clave: string
  nombre: string;
}

export interface GetSucursalesProps {
  all?: boolean
}

export const getSucursales = async (props?: GetSucursalesProps) => {
  const api = await getAccountsApi();
  if (!api) return null;
  const qs = objectToQueryStringWithArray(props);
  const response = await api.get(
    qs
      ? `/Sucursales/GetListadoSucursales?${qs}`
      : `/Sucursales/GetListadoSucursales`,
  );

  return response.data;
};

export interface GetSaldoEnCvuProps {
  cvu: string;
}

export const getSaldoEnCvu = async (props: GetSaldoEnCvuProps) => {
  const api = await getAccountsApi();
  if (!api) return null;
  const response = await api.get(
    `/Cvu/${props.cvu}/Saldo`,
  );

  return response.data;
};
